import { ref, onMounted } from "vue";
import { useRegisterSW } from "virtual:pwa-register/vue";

// Types for PWA events
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function usePWAUpdate() {
  const {
    offlineReady: pwaOfflineReady,
    needRefresh: pwaNeedRefresh,
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, r) {
      console.log(`Service Worker at: ${swUrl}`);
      // Check for updates periodically
      r &&
        setInterval(async () => {
          console.log("Checking for sw update");
          await r.update();
        }, 60 * 60 * 1000); // 1 hour
    },
  });

  const showUpdatePrompt = pwaNeedRefresh;
  const offlineReady = pwaOfflineReady;

  const isInstallable = ref(false);
  const installPrompt = ref<BeforeInstallPromptEvent | null>(null);

  // Safari-specific installation state
  const isSafari = ref(false);
  const showSafariInstallPrompt = ref(false);
  const hasDismissedSafariPrompt = ref(false);

  // General installation state
  const hasDismissedInstallPrompt = ref(false);

  // Safari/iOS detection
  const detectSafari = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    // Detect iOS devices (iPhone, iPad, iPod)
    const isIOS =
      /ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream;

    // Detect Safari desktop (must contain 'safari' but NOT contain 'chrome', 'chromium', 'edg', 'firefox', 'opera')
    const isSafariDesktop =
      userAgent.includes("safari") &&
      !userAgent.includes("chrome") &&
      !userAgent.includes("chromium") &&
      !userAgent.includes("edg") &&
      !userAgent.includes("firefox") &&
      !userAgent.includes("opera") &&
      !userAgent.includes("opr"); // Opera also uses 'opr'
    return isIOS || isSafariDesktop;
  };

  // Check if app is already installed (for Safari)
  const isStandalone = (): boolean => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    );
  };

  // Handle Safari installation prompt logic
  const checkSafariInstallPrompt = () => {
    if (detectSafari() && !isStandalone() && !hasDismissedSafariPrompt.value) {
      // Check if we should show the prompt (not too frequently)
      const lastPrompt = localStorage.getItem("safari-pwa-prompt-dismissed");
      const daysSinceLastPrompt = lastPrompt
        ? (Date.now() - parseInt(lastPrompt)) / (1000 * 60 * 60 * 24)
        : 999;

      if (daysSinceLastPrompt > 7) {
        // Show again after 7 days
        setTimeout(() => {
          showSafariInstallPrompt.value = true;
        }, 3000); // Show after 3 seconds to not be intrusive
      }
    }
  };

  const dismissSafariInstall = () => {
    showSafariInstallPrompt.value = false;
    hasDismissedSafariPrompt.value = true;
    localStorage.setItem("safari-pwa-prompt-dismissed", Date.now().toString());
  };

  // Handle app installation
  const handleInstallPrompt = (e: Event) => {
    e.preventDefault();
    installPrompt.value = e as BeforeInstallPromptEvent;

    // Check if user has dismissed install prompt recently
    const lastDismissed = localStorage.getItem("pwa-install-prompt-dismissed");
    const daysSinceLastDismissal = lastDismissed
      ? (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24)
      : 999;

    // Only show if it's been more than 7 days since last dismissal
    if (daysSinceLastDismissal > 7) {
      isInstallable.value = true;
    } else {
      hasDismissedInstallPrompt.value = true;
    }
  };

  const installApp = async () => {
    if (installPrompt.value) {
      await installPrompt.value.prompt();
      const choiceResult = await installPrompt.value.userChoice;
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
        isInstallable.value = false;
        installPrompt.value = null;
        // Clear any dismissal records since user installed
        localStorage.removeItem("pwa-install-prompt-dismissed");
      } else {
        // User dismissed the native prompt, treat as dismissal
        dismissInstall();
      }
    }
  };

  const dismissInstall = () => {
    isInstallable.value = false;
    installPrompt.value = null;
    hasDismissedInstallPrompt.value = true;
    localStorage.setItem("pwa-install-prompt-dismissed", Date.now().toString());
  };

  const updateApp = async () => {
    await updateServiceWorker(true);
  };

  const dismissUpdate = () => {
    showUpdatePrompt.value = false;
  };

  onMounted(() => {
    // Detect Safari
    isSafari.value = detectSafari();
    console.log("Is Safari:", isSafari.value);
    // Check Safari install prompt
    checkSafariInstallPrompt();

    // Listen for install prompt (non-Safari browsers)
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);

    // Listen for successful app install
    window.addEventListener("appinstalled", () => {
      console.log("App installed successfully");
      isInstallable.value = false;
      installPrompt.value = null;
    });
  });

  return {
    // Update related
    showUpdatePrompt,
    offlineReady,
    needRefresh: pwaNeedRefresh, // keep for compatibility if used elsewhere
    updateApp,
    dismissUpdate,
    checkForUpdates: async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
      }
    },

    // Install related
    isInstallable,
    installApp,
    dismissInstall,

    // Safari specific
    isSafari,
    showSafariInstallPrompt,
    dismissSafariInstall,
  };
}
